import angular, { Injectable, IPromise } from "angular";
import { RecordDataSource } from "../../grids";
import { ColDef } from "../../grids/columns/colDef";
import { IGridSettings } from "../../grids/settings/gridSettingsService";
import { cloneDeep, lowerCase } from "lodash";
import {
  ColumnDefinitionSetService,
  vsServiceName as ColumnDefinitionSetServiceName,
} from "../../grids/columns/columnDefinitionSetService";
import { IInjectable } from "@uirouter/angularjs";

export interface GridConfigurationFactorySet {
  [name: string]: Injectable<GridConfigurationFactory>;
}

export type GridConfigurationFactory =
  | (new (...args: any[]) => GridConfiguration)
  // Instead of classes, plain functions are often used as controller constructors, especially in examples.
  | ((...args: any[]) => GridConfiguration);

export interface GridConfiguration {
  columns?: ColDef[];
  columnDefinitionSet?: string;
  onItemCommand?: Injectable<(...args: any[]) => IPromise<void>>;
  gridDataSource?: RecordDataSource;
  defaultSort?: string;
  nameSingular?: string;
  namePlural?: string;
  showAddButton?: boolean;
  showAddButtonPermission?: string | string[];
  gridSelectionChanged?: Injectable<(...args: any[]) => void>;
  showSelectButtons?: boolean;
  gridSettings?: IGridSettings;
  initialSelectedIds?: number[];
  onAddNew?: () => IPromise<void>;
}

export const vsServiceName = "recordGridViewService";
export const vsProviderName = `${vsServiceName}Provider`;

export class RecordGridViewService {
  constructor(
    private readonly $inject: angular.auto.IInjectorService,
    private readonly configs: GridConfigurationFactorySet,
    private readonly columnDefinitionSetService: ColumnDefinitionSetService
  ) {}

  static combineSort(left?: string, right?: string): string {
    if (!left) {
      return right ?? "";
    }
    if (!right) {
      return left ?? "";
    }

    return `${left},${right}`;
  }

  getGridConfiguration(name: string): GridConfiguration | undefined {
    const gridConfigurationFactory =
      this.configs[RecordGridViewService.normalizeName(name)];
    if (gridConfigurationFactory === undefined) return undefined;

    var configs = RecordGridViewService.getGridConfigurations(
      this.$inject,
      gridConfigurationFactory,
      this.columnDefinitionSetService
    );

    return configs;
  }

  static getResolveObjectFromGridConfiguration(
    gridConfigurationFactory: Injectable<(...args: any[]) => GridConfiguration>
  ): {
    [key: string]: IInjectable;
  } {
    return {
      gridConfiguration: gridConfigurationFactory,
      gridDataSource: [
        "gridConfiguration",
        (gridConfiguration: GridConfiguration) =>
          gridConfiguration.gridDataSource,
      ],
      gridSettings: [
        "gridConfiguration",
        (gridConfiguration: GridConfiguration) =>
          gridConfiguration.gridSettings,
      ],
      columns: [
        "gridConfiguration",
        (gridConfiguration: GridConfiguration) => gridConfiguration.columns,
      ],
      defaultSort: [
        "gridConfiguration",
        (gridConfiguration: GridConfiguration) => gridConfiguration.defaultSort,
      ],
      nameSingular: [
        "gridConfiguration",
        (gridConfiguration: GridConfiguration) =>
          gridConfiguration.nameSingular,
      ],
      namePlural: [
        "gridConfiguration",
        (gridConfiguration: GridConfiguration) => gridConfiguration.namePlural,
      ],
      showAddButton: [
        "gridConfiguration",
        (gridConfiguration: GridConfiguration) =>
          gridConfiguration.showAddButton,
      ],
      showAddButtonPermission: [
        "gridConfiguration",
        (gridConfiguration: GridConfiguration) =>
          gridConfiguration.showAddButtonPermission,
      ],
      onItemCommand: [
        "gridConfiguration",
        (gridConfiguration: GridConfiguration) =>
          gridConfiguration.onItemCommand,
      ],
      gridSelectionChanged: [
        "gridConfiguration",
        (gridConfiguration: GridConfiguration) =>
          gridConfiguration.gridSelectionChanged,
      ],
      showSelectButtons: [
        "gridConfiguration",
        (gridConfiguration: GridConfiguration) =>
          gridConfiguration.showSelectButtons,
      ],
      initialSelectedIds: [
        "gridConfiguration",
        (gridConfiguration: GridConfiguration) =>
          gridConfiguration.initialSelectedIds,
      ],
      onAddNew: [
        "gridConfiguration",
        (gridConfiguration: GridConfiguration) => gridConfiguration.onAddNew,
      ],
    };
  }

  private static getGridConfigurations(
    $injector: angular.auto.IInjectorService,
    config: Injectable<GridConfigurationFactory>,
    columnDefinitionSetService: ColumnDefinitionSetService
  ): GridConfiguration {
    const gridConfig = $injector.invoke(config);
    if (gridConfig.columnDefinitionSet && !gridConfig.columns) {
      gridConfig.columns = columnDefinitionSetService.getColumnDefinitionSet(
        gridConfig.columnDefinitionSet
      );
    }

    gridConfig.columns = cloneDeep(gridConfig.columns);

    return gridConfig;
  }

  static normalizeName(name: string): string {
    return lowerCase(name);
  }
}

export class RecordGridViewServiceProvider implements angular.IServiceProvider {
  private readonly _configs: GridConfigurationFactorySet = {};

  addGridConfiguration(
    name: string,
    config: Injectable<GridConfigurationFactory>
  ) {
    this._configs[RecordGridViewService.normalizeName(name)] = config;
  }

  $get: any = [
    "$injector",
    ColumnDefinitionSetServiceName,
    (
      $injector: angular.auto.IInjectorService,
      columnDefinitionSetService: ColumnDefinitionSetService
    ) => {
      return new RecordGridViewService(
        $injector,
        this._configs,
        columnDefinitionSetService
      );
    },
  ];
}

export function getResolveObjectFromGridConfiguration(
  gridConfigurationFactory: IInjectable
): {
  [key: string]: IInjectable;
} {
  return {
    gridConfiguration: gridConfigurationFactory,
    gridDataSource: [
      "gridConfiguration",
      (gridConfiguration: GridConfiguration) =>
        gridConfiguration.gridDataSource,
    ],
    gridSettings: [
      "gridConfiguration",
      (gridConfiguration: GridConfiguration) => gridConfiguration.gridSettings,
    ],
    columns: [
      "gridConfiguration",
      (gridConfiguration: GridConfiguration) => gridConfiguration.columns,
    ],
    defaultSort: [
      "gridConfiguration",
      (gridConfiguration: GridConfiguration) => gridConfiguration.defaultSort,
    ],
    nameSingular: [
      "gridConfiguration",
      (gridConfiguration: GridConfiguration) => gridConfiguration.nameSingular,
    ],
    namePlural: [
      "gridConfiguration",
      (gridConfiguration: GridConfiguration) => gridConfiguration.namePlural,
    ],
    showAddButton: [
      "gridConfiguration",
      (gridConfiguration: GridConfiguration) => gridConfiguration.showAddButton,
    ],
    showAddButtonPermission: [
      "gridConfiguration",
      (gridConfiguration: GridConfiguration) =>
        gridConfiguration.showAddButtonPermission,
    ],
    onItemCommand: [
      "gridConfiguration",
      (gridConfiguration: GridConfiguration) => gridConfiguration.onItemCommand,
    ],
    gridSelectionChanged: [
      "gridConfiguration",
      (gridConfiguration: GridConfiguration) =>
        gridConfiguration.gridSelectionChanged,
    ],
    showSelectButtons: [
      "gridConfiguration",
      (gridConfiguration: GridConfiguration) =>
        gridConfiguration.showSelectButtons,
    ],
    initialSelectedIds: [
      "gridConfiguration",
      (gridConfiguration: GridConfiguration) =>
        gridConfiguration.initialSelectedIds,
    ],
    onAddNew: [
      "gridConfiguration",
      (gridConfiguration: GridConfiguration) => gridConfiguration.onAddNew,
    ],
  };
}
